import { groupBy } from 'lodash';
import { getFullName } from '@/common/utils/person';
import type { InteractionsAgent } from '@/views/Interactions/hooks/useInteractionsAgents/helpers';
import {
  AGENT_ID_FILTER,
  type InteractionFilter,
  TEAM_MEMBER_ID_FILTER,
} from '@/views/Interactions/state/filters';
import type { FilterOption } from '../../FilterAutocomplete';

/**
 * Agents with one membership render as a single row; agents with several render
 * as a display-only parent plus one child per team.
 */
export function buildGroupedAgentOptions(
  agents: InteractionsAgent[],
): FilterOption[] {
  // Grouped on `userId`, never on the facet `id`: identity leaves
  // `legacyExternalId` unset on memberships created after the migration, so
  // grouping on the facet id splits one person into a legacy group and a UUID
  // group. The facet ids themselves stay per-row, since that is what the saved
  // filters and the search facets are keyed on.
  const groupedAgents = groupBy(agents, 'userId');

  return Object.values(groupedAgents).flatMap((options) => {
    const [firstOption] = options;
    const { firstName, lastName, teamName, teamId, id, userId } = firstOption;
    const fullName = getFullName(firstName, lastName);

    if (options.length === 1) {
      return [
        {
          label: `${fullName} - ${teamName}`,
          value: `${TEAM_MEMBER_ID_FILTER}:${id}:${teamId}`,
          primaryLabel: fullName,
          secondaryLabel: teamName,
        },
      ];
    }

    const sortedOptions = [...options].sort((a, b) =>
      a.teamName.localeCompare(b.teamName),
    );

    // This option is just here for display purposes; it's `value` is never applied
    // as a facet filter. Instead, its child options are toggled when it is toggled.
    const parentOption: FilterOption = {
      label: fullName,
      value: `${AGENT_ID_FILTER}:${userId}`,
      children: sortedOptions.map((option) => ({
        label: `${fullName} - ${option.teamName}`,
        value: `${TEAM_MEMBER_ID_FILTER}:${option.id}:${option.teamId}`,
      })),
    };

    const childOptions: FilterOption[] = sortedOptions.map((option) => ({
      label: `${fullName} - ${option.teamName}`,
      primaryLabel: option.teamName,
      value: `${TEAM_MEMBER_ID_FILTER}:${option.id}:${option.teamId}`,
      parent: parentOption,
    }));

    return [parentOption, ...childOptions];
  });
}

/** `teamMemberId:<agentId>:<teamId>` → `agentId`. */
export function parseSelectedAgentIds(selected: InteractionFilter[]): string[] {
  return [
    ...new Set(
      selected
        .filter((filter) =>
          filter.value.startsWith(`${TEAM_MEMBER_ID_FILTER}:`),
        )
        .map((filter) => filter.value.split(':')[1])
        .filter(Boolean),
    ),
  ];
}
